import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cpu, Database, Brain, Trophy, Zap, Network } from "lucide-react";

export default function Landing() {
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState({ total_nodes: 0, online_nodes: 0, total_questions: 0, worker_questions: 0 });

    useEffect(() => {
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => { });
        api.get("/worker/public-stats").then((r) => setStats(r.data)).catch(() => { });
        const t = setInterval(() => {
            api.get("/worker/public-stats").then((r) => setStats(r.data)).catch(() => { });
        }, 8000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="relative">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-24 relative">
                    <div className="grid lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-8 stagger-in">
                            <span className="overline text-primary" data-testid="hero-eyebrow">
                                ▮ Rank &lt; 1000 · Distributed Compute · AI Analytics
                            </span>
                            <h1 className="mt-6 font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                                {settings?.hero_headline || "Crack NEET/JEE with"}
                                <br />
                                <span className="text-primary">AI-generated</span> practice.
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl">
                                {settings?.hero_subtext || "Unlimited exam-grade questions, live distributed compute, and Illume AI analytics."}
                                <span className="block mt-2 font-mono text-sm">First {settings?.free_generation_limit || 15} generations + AI analytics are on the house.</span>
                            </p>

                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/register">
                                    <Button data-testid="hero-cta-register" size="lg" className="rounded-sm bg-primary hover:bg-primary/90 btn-press h-12 px-6">
                                        Start solving <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                                <Link to="/desktop">
                                    <Button data-testid="hero-cta-desktop" size="lg" variant="outline" className="rounded-sm btn-press h-12 px-6">
                                        <Cpu className="w-4 h-4 mr-2" /> Contribute compute
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-4">
                            <div className="border border-border rounded-sm p-6 bg-card/60 relative overflow-hidden">
                                <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-primary/10 blur-2xl" />
                                <span className="overline text-muted-foreground">Live Network</span>
                                <div className="mt-4 grid grid-cols-2 gap-4 font-mono">
                                    <Stat label="Online nodes" value={stats.online_nodes} accent />
                                    <Stat label="Total nodes" value={stats.total_nodes} />
                                    <Stat label="Bank size" value={stats.total_questions} />
                                    <Stat label="Worker gens" value={stats.worker_questions} />
                                </div>
                                <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="pulse-dot text-green-500">●</span>
                                    <span className="font-mono">MongoDB Vector Bank · syncing</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* marquee ticker */}
                    <div className="mt-16 border-y border-border py-3 overflow-hidden">
                        <div className="flex marquee gap-12 whitespace-nowrap font-mono text-xs uppercase tracking-widest text-muted-foreground">
                            {Array(2).fill(0).map((_, i) => (
                                <React.Fragment key={i}>
                                    <span>▮ NEET 2026</span>
                                    <span>▮ JEE Main</span>
                                    <span>▮ JEE Advanced</span>
                                    <span>▮ Physics · Chemistry · Biology · Math</span>
                                    <span>▮ NTA-style interface</span>
                                    <span>▮ Distributed compute</span>
                                    <span>▮ AI diagnostics</span>
                                    <span>▮ Vector retrieval</span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                <span className="overline text-muted-foreground">01 · The Engine</span>
                <h2 className="mt-3 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-3xl">
                    A study platform architected like <span className="text-primary">a research cluster.</span>
                </h2>

                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in">
                    <Pillar icon={<Brain />} title="Pluggable LLMs" desc="Students and admins can use DeepSeek, OpenAI, Claude, or Ollama credentials." />
                    <Pillar icon={<Network />} title="BOINC-style workers" desc="Idle desktops across the world generate questions when your CPU is free." />
                    <Pillar icon={<Database />} title="Vector question bank" desc="Retrieval-first: every generation seeded from a growing MongoDB vector store." />
                    <Pillar icon={<Trophy />} title="NTA-grade scoring" desc="+4 / -1 · timers · palette · exactly like the real exam." />
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-t border-border bg-muted/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                    <span className="overline text-muted-foreground">02 · Flow</span>
                    <h2 className="mt-3 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-3xl">
                        Generate → Solve → Score → <span className="text-secondary">Diagnose.</span>
                    </h2>

                    <div className="mt-12 grid md:grid-cols-4 gap-0 border border-border rounded-sm overflow-hidden">
                        {[
                            { n: "01", t: "Pick your exam", d: "NEET or JEE, subjects, difficulty, count." },
                            { n: "02", t: "Vector retrieve", d: "Questions pulled from the bank, backfilled by LLM." },
                            { n: "03", t: "Solve NTA-style", d: "Palette, timer, mark for review, keyboard nav." },
                            { n: "04", t: "AI diagnosis", d: "Weakness heatmap + 7-day plan by an AI coach." },
                        ].map((s) => (
                            <div key={s.n} className="p-6 border-r border-border last:border-r-0 hover:bg-background transition-colors">
                                <span className="font-mono text-xs text-primary">{s.n}</span>
                                <h3 className="mt-4 font-heading font-bold text-lg">{s.t}</h3>
                                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                <span className="overline text-muted-foreground">03 · Pricing</span>
                <h2 className="mt-3 font-heading font-black text-3xl sm:text-4xl tracking-tighter">Free forever. Analytics on tap.</h2>

                <div className="mt-12 grid md:grid-cols-2 gap-4">
                    <div className="border border-border rounded-sm p-8">
                        <span className="overline text-muted-foreground">Free tier</span>
                        <div className="mt-2 font-heading font-black text-4xl">₹0</div>
                        <ul className="mt-6 space-y-2 text-sm">
                            <li>▮ Unlimited question generation & scoring</li>
                            <li>▮ First {settings?.free_ai_analytics_limit || 15} AI analytics reports free</li>
                            <li>▮ Full NTA-style exam interface</li>
                            <li>▮ Cross-device history</li>
                        </ul>
                    </div>
                    <div className="border-2 border-primary rounded-sm p-8 relative bg-card">
                        <span className="absolute -top-3 left-8 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm">Prepcheck Plus</span>
                        <span className="overline text-primary">Analytics unlocked</span>
                        <div className="mt-2 font-heading font-black text-4xl">
                            {settings?.currency === "INR" ? "₹" : "$"}{settings?.subscription_price_monthly || 299}
                            <span className="text-base font-normal text-muted-foreground">/month</span>
                        </div>
                        <ul className="mt-6 space-y-2 text-sm">
                            <li>▮ <b>Unlimited</b> AI-powered diagnostics</li>
                            <li>▮ Personalized 7-day study plans</li>
                            <li>▮ Exam-readiness score</li>
                            <li>▮ Priority queue on distributed compute</li>
                        </ul>
                        <Link to="/register">
                            <Button data-testid="pricing-cta" className="w-full mt-8 rounded-sm bg-primary hover:bg-primary/90 btn-press">
                                Start free · upgrade later <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

const Stat = ({ label, value, accent }) => (
    <div>
        <div className={`font-heading font-black text-3xl ${accent ? "text-primary" : ""}`}>{value ?? "—"}</div>
        <div className="overline text-[9px] text-muted-foreground mt-1">{label}</div>
    </div>
);

const Pillar = ({ icon, title, desc }) => (
    <div className="border border-border rounded-sm p-6 hover:border-primary/60 transition-colors">
        <div className="w-10 h-10 rounded-sm border border-border flex items-center justify-center text-primary">
            {React.cloneElement(icon, { className: "w-5 h-5" })}
        </div>
        <h3 className="mt-4 font-heading font-bold text-lg">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
    </div>
);
